function looksLikeCompleteCarePlan(current, replacement) {
  const source = String(current || '');
  const next = String(replacement || '');
  if (!source.trim() || !next.trim()) return false;
  if (source.length > 1000 && next.length < 500) return false;

  const headings = [
    'PROBLEM LIST',
    'MEASURABLE TREATMENT GOALS',
    'MANAGEMENT PLAN',
    'CARE COORDINATION',
    'REVIEW AND REVISION',
  ];
  // CARE COORDINATION is omittable when no specialists/care transitions are
  // documented, so a revision may drop it without being treated as truncated.
  const optionalHeadings = new Set(['CARE COORDINATION']);
  const sourceUpper = source.toUpperCase();
  const nextUpper = next.toUpperCase();
  const present = headings.filter(h => sourceUpper.includes(h));
  if (present.length < 3) return true;
  return present
    .filter(h => !optionalHeadings.has(h))
    .every(h => nextUpper.includes(h));
}

function normalizeProtectedValue(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/≤/g, '<=')
    .replace(/≥/g, '>=')
    .replace(/\s+/g, '')
    .replace(/[;,.]+$/g, '');
}

function extractProtectedValues(text) {
  const source = String(text || '');
  const values = new Set();
  const patterns = [
    /\b\d{4}-\d{2}-\d{2}\b/g,
    /\b(?:G|CPT)\s*\d{4,5}\b/gi,
    /\b\d{5}\b/g,
    /(?:<=|>=|<|>|less than|greater than|at least|no more than|under|over)?\s*\b\d+(?:\.\d+)?\s*(?:%|mg\/dL|mg|mmHg|min|minutes?|m|score)?\b/gi,
  ];

  patterns.forEach((pattern) => {
    const matches = source.match(pattern) || [];
    matches.forEach((match) => {
      const normalized = normalizeProtectedValue(match);
      if (normalized) values.add(normalized);
    });
  });

  return values;
}

function permitsProtectedValueChanges(instruction) {
  const text = String(instruction || '').toLowerCase();
  const changeVerb = /\b(change|update|set|adjust|raise|lower|increase|decrease|replace)\b/.test(text);
  if (!changeVerb) return false;
  return /\b(value|target|threshold|goal|score|date|dose|mg|mmhg|a1c|ldl|phq|minutes?|mins?)\b/.test(text) ||
    /\bto\s*(?:<=|>=|<|>|less than|greater than)?\s*\d/.test(text);
}

function diffProtectedValues(current, replacement) {
  const before = extractProtectedValues(current);
  const after = extractProtectedValues(replacement);
  return {
    missing: [...before].filter(value => !after.has(value)),
    added: [...after].filter(value => !before.has(value)),
  };
}

function assessCarePlanRevision(current, replacement, instruction) {
  if (!looksLikeCompleteCarePlan(current, replacement)) {
    return { canApply: false, reason: 'partial', missing: [], added: [] };
  }
  if (permitsProtectedValueChanges(instruction)) {
    return { canApply: true, reason: '', missing: [], added: [] };
  }
  const diff = diffProtectedValues(current, replacement);
  if (diff.missing.length || diff.added.length) {
    return { canApply: false, reason: 'protected-values', ...diff };
  }
  return { canApply: true, reason: '', missing: [], added: [] };
}

module.exports = {
  assessCarePlanRevision,
  diffProtectedValues,
  extractProtectedValues,
  looksLikeCompleteCarePlan,
  permitsProtectedValueChanges,
};
